/* =============================================================================
 * ISMONT – Produkt-Datenblatt (PDF)
 * Datei: assets/ismont-datenblatt.js
 * Version: 1.0.0
 *
 * Erzeugt clientseitig ein A4-PDF aus der im Liquid-Snippet hinterlegten
 * Datenblatt-Vorlage. Keine externe App, keine Serverkosten.
 *
 * Ablauf beim Klick:
 *   1. Button sperren + Ladezustand anzeigen
 *   2. html2pdf.js (und optional qrcode.js) einmalig vom CDN nachladen
 *   3. Erstelldatum + QR-Code in die Vorlage schreiben
 *   4. Warten, bis Logo und Produktbild vollständig geladen sind
 *   5. PDF rendern und als [handle]-datenblatt.pdf speichern
 *   6. Button zurücksetzen (auch im Fehlerfall)
 * ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * Konfiguration
   * Über window.ISMONT_DATASHEET_CONFIG vor dem Laden dieses Skripts
   * überschreibbar (siehe Dokumentation, Abschnitt "Anpassungshinweise").
   * ------------------------------------------------------------------- */
  var DEFAULTS = {
    html2pdfSrc: 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.14.0/html2pdf.bundle.min.js',
    html2pdfFallbackSrc: 'https://cdn.jsdelivr.net/npm/html2pdf.js@0.10.3/dist/html2pdf.bundle.min.js',
    qrcodeSrc: 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js',
    enableQrCode: true,
    scale: 2,            // Rendering-Skalierung: 2 = scharf, 3 = schärfer/langsamer
    margin: 10,          // Seitenrand in mm
    imageQuality: 0.98,
    imageTimeout: 8000,  // ms, bis das Warten auf Bilder abgebrochen wird
    locale: 'de-DE'
  };

  var CONFIG = Object.assign({}, DEFAULTS, window.ISMONT_DATASHEET_CONFIG || {});

  /* ---------------------------------------------------------------------
   * Übersetzungen – Mehrsprachigkeits-Vorbereitung
   * Zusätzliche Sprachen können über window.ISMONT_DATASHEET_I18N ergänzt
   * oder aus Shopify-Locale-Dateien befüllt werden.
   * ------------------------------------------------------------------- */
  var STRINGS = Object.assign({
    de: {
      idle: '📄 Datenblatt anzeigen / herunterladen',
      loading: '⏳ Datenblatt wird erstellt...',
      success: 'Datenblatt wurde heruntergeladen.',
      errorLib: 'Das PDF-Modul konnte nicht geladen werden. Bitte Werbeblocker deaktivieren und erneut versuchen.',
      errorGeneric: 'Das Datenblatt konnte nicht erstellt werden. Bitte versuchen Sie es erneut.'
    },
    en: {
      idle: '📄 View / download data sheet',
      loading: '⏳ Creating data sheet...',
      success: 'Data sheet downloaded.',
      errorLib: 'The PDF module could not be loaded. Please disable your ad blocker and try again.',
      errorGeneric: 'The data sheet could not be created. Please try again.'
    }
  }, window.ISMONT_DATASHEET_I18N || {});

  function t(key) {
    var lang = (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
    var pack = STRINGS[lang] || STRINGS.de;
    return pack[key] || STRINGS.de[key] || '';
  }

  /* ---------------------------------------------------------------------
   * Hilfsfunktion: Skript einmalig laden (mit Promise-Cache)
   * ------------------------------------------------------------------- */
  var scriptCache = {};

  function loadScript(src) {
    if (scriptCache[src]) return scriptCache[src];

    scriptCache[src] = new Promise(function (resolve, reject) {
      var el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.crossOrigin = 'anonymous';
      el.onload = function () { resolve(); };
      el.onerror = function () {
        delete scriptCache[src];               // erneuter Versuch bleibt möglich
        reject(new Error('Script konnte nicht geladen werden: ' + src));
      };
      document.head.appendChild(el);
    });

    return scriptCache[src];
  }

  /* Lädt html2pdf.js – erst cdnjs, bei Fehler jsDelivr als Fallback */
  function ensureHtml2Pdf() {
    if (typeof window.html2pdf === 'function') return Promise.resolve();

    return loadScript(CONFIG.html2pdfSrc)
      .catch(function () { return loadScript(CONFIG.html2pdfFallbackSrc); })
      .then(function () {
        if (typeof window.html2pdf !== 'function') {
          throw new Error('html2pdf global nicht verfügbar');
        }
      });
  }

  function ensureQrCode() {
    if (!CONFIG.enableQrCode) return Promise.resolve(false);
    if (typeof window.QRCode === 'function') return Promise.resolve(true);

    return loadScript(CONFIG.qrcodeSrc)
      .then(function () { return typeof window.QRCode === 'function'; })
      .catch(function () { return false; });      // QR-Code ist optional – kein Abbruch
  }

  /* ---------------------------------------------------------------------
   * Hilfsfunktion: Warten, bis alle Bilder im Datenblatt geladen sind.
   * Ohne diesen Schritt erscheinen Logo/Produktbild im PDF gelegentlich leer.
   * ------------------------------------------------------------------- */
  function waitForImages(root) {
    var images = Array.prototype.slice.call(root.querySelectorAll('img'));
    if (!images.length) return Promise.resolve();

    var jobs = images.map(function (img) {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();

      return new Promise(function (resolve) {
        var done = false;
        function finish() {
          if (done) return;
          done = true;
          img.removeEventListener('load', finish);
          img.removeEventListener('error', finish);
          resolve();
        }
        img.addEventListener('load', finish);
        img.addEventListener('error', finish);   // fehlerhafte Bilder blockieren nicht
        setTimeout(finish, CONFIG.imageTimeout);
      });
    });

    return Promise.all(jobs);
  }

  /* ---------------------------------------------------------------------
   * Logo-Fallback: lädt das Logobild nicht (falsches Setting, gelöschte
   * Datei), wird stattdessen der Shopname als Schriftzug gezeigt.
   * Muss NACH waitForImages laufen.
   * ------------------------------------------------------------------- */
  function applyLogoFallback(sheet) {
    var logo = sheet.querySelector('[data-ismont-logo]');
    var fallback = sheet.querySelector('[data-ismont-logo-fallback]');
    if (!logo || !fallback) return;

    if (logo.naturalWidth === 0) {
      logo.remove();
      fallback.hidden = false;
    }
  }

  /* ---------------------------------------------------------------------
   * Datenblatt vorbereiten: Erstelldatum und QR-Code einsetzen
   * ------------------------------------------------------------------- */
  function stampDate(sheet) {
    var target = sheet.querySelector('[data-ismont-date]');
    if (!target) return;

    target.textContent = new Date().toLocaleDateString(CONFIG.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  function renderQrCode(sheet, available) {
    var holder = sheet.querySelector('[data-ismont-qr]');
    if (!holder) return;

    var url = sheet.getAttribute('data-product-url');

    // Nicht verfügbar oder keine URL: Fläche freihalten, damit der Fußtext
    // seine Position behält (visibility statt display).
    if (!available || !url) {
      holder.style.visibility = 'hidden';
      return;
    }

    if (holder.getAttribute('data-rendered') === 'true') return;

    try {
      /* global QRCode */
      new QRCode(holder, {
        text: url,
        width: 140,                 // doppelte Anzeigegröße = scharf im PDF
        height: 140,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.M
      });
      holder.setAttribute('data-rendered', 'true');
    } catch (err) {
      holder.style.visibility = 'hidden';
    }
  }

  /* ---------------------------------------------------------------------
   * Button-Zustände
   * ------------------------------------------------------------------- */
  function setBusy(button, busy) {
    var label = button.querySelector('.ismont-ds-button__label') || button;

    if (busy) {
      button.dataset.originalLabel = label.textContent;
      label.textContent = t('loading');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      label.textContent = button.dataset.originalLabel || t('idle');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  function setStatus(button, message, state) {
    var status = button.parentElement
      ? button.parentElement.querySelector('[data-ismont-datasheet-status]')
      : null;
    if (!status) return;

    if (!message) {
      status.hidden = true;
      status.textContent = '';
      status.removeAttribute('data-state');
      return;
    }

    status.textContent = message;
    status.setAttribute('data-state', state || 'info');
    status.hidden = false;

    if (state !== 'error') {
      setTimeout(function () {
        status.hidden = true;
        status.textContent = '';
      }, 6000);
    }
  }

  /* ---------------------------------------------------------------------
   * Hauptfunktion: PDF erzeugen
   * ------------------------------------------------------------------- */
  function generatePdf(button) {
    var sheetId = button.getAttribute('data-sheet-target');
    var sheet = document.getElementById(sheetId);

    if (!sheet) {
      setStatus(button, t('errorGeneric'), 'error');
      return;
    }

    var filename = (button.getAttribute('data-filename') || 'datenblatt') + '.pdf';

    setBusy(button, true);
    setStatus(button, null);

    ensureHtml2Pdf()
      .catch(function (err) {
        err.ismontReason = 'lib';
        throw err;
      })
      .then(function () {
        return ensureQrCode();
      })
      .then(function (qrAvailable) {
        stampDate(sheet);
        renderQrCode(sheet, qrAvailable);
        return waitForImages(sheet);
      })
      .then(function () {
        applyLogoFallback(sheet);

        return window.html2pdf()
          .set({
            margin: CONFIG.margin,
            filename: filename,
            image: { type: 'jpeg', quality: CONFIG.imageQuality },
            html2canvas: {
              scale: CONFIG.scale,
              useCORS: true,          // Shopify-CDN liefert Access-Control-Allow-Origin: *
              allowTaint: false,
              backgroundColor: '#ffffff',
              logging: false,
              imageTimeout: CONFIG.imageTimeout
            },
            jsPDF: {
              unit: 'mm',
              format: 'a4',
              orientation: 'portrait',
              compress: true
            },
            pagebreak: { mode: ['avoid-all', 'css'] }
          })
          .from(sheet.querySelector('.ismont-ds-page') || sheet)
          .save();
      })
      .then(function () {
        setStatus(button, t('success'), 'success');
      })
      .catch(function (err) {
        // eslint-disable-next-line no-console
        console.error('[ISMONT Datenblatt]', err);
        setStatus(
          button,
          err && err.ismontReason === 'lib' ? t('errorLib') : t('errorGeneric'),
          'error'
        );
      })
      .then(function () {
        setBusy(button, false);      // läuft in jedem Fall (Erfolg wie Fehler)
      });
  }

  /* ---------------------------------------------------------------------
   * Initialisierung
   * Event-Delegation am document: funktioniert auch für Buttons, die per
   * Section-Rendering-API (Variantenwechsel, Quick-View) nachgeladen werden.
   * ------------------------------------------------------------------- */
  function init() {
    if (document.documentElement.hasAttribute('data-ismont-ds-init')) return;
    document.documentElement.setAttribute('data-ismont-ds-init', 'true');

    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-ismont-datasheet-trigger]');
      if (!button || button.disabled) return;

      event.preventDefault();
      generatePdf(button);
    });

    /* Performance: html2pdf.js beim ersten Hover/Fokus vorladen, damit der
       eigentliche Klick ohne Wartezeit auf das CDN auskommt. */
    ['mouseenter', 'focusin', 'touchstart'].forEach(function (evt) {
      document.addEventListener(evt, function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        if (!target.closest('[data-ismont-datasheet-trigger]')) return;
        ensureHtml2Pdf().catch(function () { /* stiller Fehlschlag, Klick versucht es erneut */ });
      }, { capture: true, passive: true, once: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
